import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { ModalPortal } from '@/components/ModalLayer';

/**
 * RewardPopup — scaffold-only component (Feb 2026).
 *
 * Controlled correction pass: this file is the UI scaffold for the
 * hidden reward system. Three state copy strings are wired, nothing
 * else. Triggers are intentionally DORMANT — no payment hook, no job
 * hook, no lead hook. A future pass will decide where to fire events.
 *
 * How triggering will work (future, NOT implemented here):
 *   window.dispatchEvent(new CustomEvent('remod:reward', {
 *     detail: { state: 'started' | 'progressing' | 'unlocked' }
 *   }))
 *
 * HARD RULES enforced in this component:
 *   • No numbers, thresholds, caps, or counts shown to the user.
 *   • No reward-type labels (never says "free lead", "discount", etc.).
 *   • No explanation of mechanics.
 *   • One tap to dismiss. Nothing else.
 *
 * Z-index (locked 2026-05-30): renders through the global ModalPortal
 * (z-9000+) so it always sits above maps, workspace, and sidebars.
 */
const COPY = {
  started:     "You've started earning rewards.",
  progressing: "You're making progress toward your next reward.",
  unlocked:    "You've unlocked a reward.",
};

const RewardPopup = () => {
  const [state, setState] = useState(null); // 'started' | 'progressing' | 'unlocked' | null

  useEffect(() => {
    const handler = (e) => {
      const s = e?.detail?.state;
      if (s === 'started' || s === 'progressing' || s === 'unlocked') {
        setState(s);
      }
    };
    window.addEventListener('remod:reward', handler);
    return () => window.removeEventListener('remod:reward', handler);
  }, []);

  if (!state) return null;

  return (
    <ModalPortal>
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[320px] max-w-[calc(100vw-2rem)]"
        data-testid="reward-popup"
      >
        <div
          className="rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
          style={{ boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)' }}
        >
          <div className="flex items-start gap-3 p-4">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <p
              className="text-[13px] font-semibold text-slate-900 leading-snug flex-1"
              data-testid={`reward-popup-message-${state}`}
            >
              {COPY[state]}
            </p>
            <button
              type="button"
              onClick={() => setState(null)}
              data-testid="reward-popup-dismiss"
              className="text-[12px] font-medium text-slate-500 hover:text-slate-700"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default RewardPopup;
